import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Trophy, TrendingUp, TrendingDown, Target, Star,
  ArrowUp, ArrowDown, Zap, Medal, Crown, Flame, Award,
  ChevronRight, Sparkles, BarChart2, Users, Calendar,
  ChevronUp, Rocket, Shield, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Leaderboard = ({ user, onLogout }) => {
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [viewMode, setViewMode] = useState('current');

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-blue-100">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <Trophy className="w-8 h-8 text-amber-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-slate-600 mt-6 font-medium text-lg">Loading Leaderboard...</p>
          </div>
        </main>
      </div>
    );
  }

  const leaderboard = leaderboardData?.leaderboard || [];
  const currentMonth = leaderboardData?.current_month || {};
  const lastMonth = leaderboardData?.last_month || {};
  
  const sortedLeaderboard = viewMode === 'current' 
    ? [...leaderboard].sort((a, b) => b.current_month - a.current_month)
    : [...leaderboard].sort((a, b) => b.last_month - a.last_month);

  const champion = sortedLeaderboard[0];
  const runnerUp = sortedLeaderboard[1];
  const thirdPlace = sortedLeaderboard[2];
  const currentUserData = leaderboard.find(a => a.email === user?.email);
  const currentUserRank = sortedLeaderboard.findIndex(a => a.email === user?.email) + 1;
  const totalSales = leaderboard.reduce((sum, a) => sum + (viewMode === 'current' ? a.current_month : a.last_month), 0);
  const avgSales = leaderboard.length > 0 ? Math.round(totalSales / leaderboard.length) : 0;

  // Calculate gap to next rank for current user
  const userAheadOfCurrentUser = currentUserRank > 1 ? sortedLeaderboard[currentUserRank - 2] : null;
  const gapToNextRank = userAheadOfCurrentUser 
    ? (viewMode === 'current' ? userAheadOfCurrentUser.current_month : userAheadOfCurrentUser.last_month) - 
      (viewMode === 'current' ? currentUserData?.current_month : currentUserData?.last_month)
    : 0;

  // Get rank suffix
  const getRankSuffix = (rank) => {
    if (rank === 1) return 'st';
    if (rank === 2) return 'nd';
    if (rank === 3) return 'rd';
    return 'th';
  };

  // Get progress percentage to next rank
  const getProgressToNextRank = () => {
    if (!userAheadOfCurrentUser || !currentUserData) return 100;
    const currentSales = viewMode === 'current' ? currentUserData.current_month : currentUserData.last_month;
    const aheadSales = viewMode === 'current' ? userAheadOfCurrentUser.current_month : userAheadOfCurrentUser.last_month;
    if (aheadSales === 0) return 100;
    return Math.min(100, Math.round((currentSales / aheadSales) * 100));
  };

  return (
    <div className="flex min-h-screen bg-blue-100">
      <Sidebar user={user} onLogout={onLogout} />
      
      <main className="flex-1 overflow-auto" data-testid="leaderboard-page">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2.5 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl shadow-lg shadow-amber-500/20">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
                      Performance Leaderboard
                    </h1>
                    <p className="text-slate-500 text-sm mt-0.5">Recognizing excellence in sales performance</p>
                  </div>
                </div>
              </div>

              {/* Period Toggle - Professional Pills */}
              <div className="flex items-center gap-2 p-1.5 bg-white rounded-xl border border-slate-200 shadow-sm">
                <button
                  onClick={() => setViewMode('current')}
                  className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                    viewMode === 'current'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{currentMonth.name}</span>
                  </div>
                  <span className="text-xs opacity-70 block mt-0.5">{currentMonth.start} - {currentMonth.end}</span>
                </button>
                <button
                  onClick={() => setViewMode('last')}
                  className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                    viewMode === 'last'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{lastMonth.name}</span>
                  </div>
                  <span className="text-xs opacity-70 block mt-0.5">{lastMonth.start} - {lastMonth.end}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Top 3 Podium Section */}
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 2nd Place */}
              {runnerUp && (
                <div className="order-2 md:order-1 flex flex-col items-center">
                  <div className="w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-6 relative overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-300 to-slate-400"></div>
                    <div className="flex flex-col items-center">
                      <div className="relative mb-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-slate-200 to-slate-400 rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-2xl font-bold text-white">{getInitials(runnerUp.agent_name)}</span>
                        </div>
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 bg-gradient-to-br from-slate-300 to-slate-500 rounded-full flex items-center justify-center shadow-md border-2 border-white">
                          <span className="text-xs font-bold text-white">2</span>
                        </div>
                      </div>
                      <Medal className="w-5 h-5 text-slate-400 mb-2" />
                      <h3 className="text-lg font-bold text-slate-800 text-center">{runnerUp.agent_name}</h3>
                      <p className="text-sm text-slate-500 capitalize mb-3">{runnerUp.role}</p>
                      <div className="text-3xl font-black text-slate-700 mb-1">
                        {viewMode === 'current' ? runnerUp.current_month : runnerUp.last_month}
                      </div>
                      <p className="text-xs text-slate-500">Sales</p>
                      <div className={`mt-3 flex items-center gap-1 text-sm font-medium ${runnerUp.growth > 0 ? 'text-emerald-600' : runnerUp.growth < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                        {runnerUp.growth > 0 ? <ArrowUp className="w-4 h-4" /> : runnerUp.growth < 0 ? <ArrowDown className="w-4 h-4" /> : null}
                        {runnerUp.growth !== 0 ? `${Math.abs(runnerUp.growth)}% vs last month` : 'No change'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 1st Place - Champion */}
              {champion && (
                <div className="order-1 md:order-2 flex flex-col items-center">
                  <div className="w-full bg-gradient-to-b from-amber-50 to-white rounded-2xl border-2 border-amber-200 shadow-lg p-6 relative overflow-hidden hover:shadow-xl transition-shadow transform md:-translate-y-4">
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400"></div>
                    <div className="absolute top-3 right-3">
                      <Sparkles className="w-5 h-5 text-amber-400" />
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative mb-4">
                        <div className="absolute -inset-2 bg-gradient-to-br from-amber-200 to-yellow-300 rounded-full blur-md opacity-60"></div>
                        <div className="relative w-24 h-24 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-xl">
                          <span className="text-3xl font-bold text-white">{getInitials(champion.agent_name)}</span>
                        </div>
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                          <Crown className="w-8 h-8 text-amber-500 drop-shadow-lg" />
                        </div>
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full shadow-lg">
                          <span className="text-xs font-bold text-white">#1</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        <Trophy className="w-5 h-5 text-amber-500" />
                        <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">Champion</span>
                      </div>
                      <h3 className="text-xl font-bold text-slate-800 text-center">{champion.agent_name}</h3>
                      <p className="text-sm text-slate-500 capitalize mb-3">{champion.role}</p>
                      <div className="text-4xl font-black bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent mb-1">
                        {viewMode === 'current' ? champion.current_month : champion.last_month}
                      </div>
                      <p className="text-xs text-slate-500">Sales</p>
                      <div className={`mt-3 flex items-center gap-1 text-sm font-semibold ${champion.growth > 0 ? 'text-emerald-600' : champion.growth < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                        {champion.growth > 0 ? <TrendingUp className="w-4 h-4" /> : champion.growth < 0 ? <TrendingDown className="w-4 h-4" /> : null}
                        {champion.growth !== 0 ? `${Math.abs(champion.growth)}% vs last month` : 'No change'}
                      </div>
                      {champion.badges?.length > 0 && (
                        <div className="flex gap-1 mt-4 flex-wrap justify-center">
                          {champion.badges.slice(0, 4).map((badge, i) => (
                            <span key={i} className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">
                              {badge.icon} {badge.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 3rd Place */}
              {thirdPlace && (
                <div className="order-3 flex flex-col items-center">
                  <div className="w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-6 relative overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-300 to-amber-400"></div>
                    <div className="flex flex-col items-center">
                      <div className="relative mb-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-orange-300 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-2xl font-bold text-white">{getInitials(thirdPlace.agent_name)}</span>
                        </div>
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center shadow-md border-2 border-white">
                          <span className="text-xs font-bold text-white">3</span>
                        </div>
                      </div>
                      <Medal className="w-5 h-5 text-orange-400 mb-2" />
                      <h3 className="text-lg font-bold text-slate-800 text-center">{thirdPlace.agent_name}</h3>
                      <p className="text-sm text-slate-500 capitalize mb-3">{thirdPlace.role}</p>
                      <div className="text-3xl font-black text-orange-600 mb-1">
                        {viewMode === 'current' ? thirdPlace.current_month : thirdPlace.last_month}
                      </div>
                      <p className="text-xs text-slate-500">Sales</p>
                      <div className={`mt-3 flex items-center gap-1 text-sm font-medium ${thirdPlace.growth > 0 ? 'text-emerald-600' : thirdPlace.growth < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                        {thirdPlace.growth > 0 ? <ArrowUp className="w-4 h-4" /> : thirdPlace.growth < 0 ? <ArrowDown className="w-4 h-4" /> : null}
                        {thirdPlace.growth !== 0 ? `${Math.abs(thirdPlace.growth)}% vs last month` : 'No change'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Your Position - Motivational Card (Only for non-top-3) */}
          {currentUserData && currentUserRank > 3 && (
            <div className="mb-8">
              <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 md:p-8">
                  <div className="flex flex-col lg:flex-row items-center gap-6">
                    {/* User Avatar & Rank */}
                    <div className="flex items-center gap-5">
                      <div className="relative">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-white/30">
                          <span className="text-2xl font-bold text-white">{getInitials(currentUserData.agent_name)}</span>
                        </div>
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 bg-white rounded-full shadow-lg">
                          <span className="text-sm font-bold text-blue-700">#{currentUserRank}</span>
                        </div>
                      </div>
                      <div className="text-white">
                        <p className="text-blue-200 text-sm font-medium uppercase tracking-wider">Your Position</p>
                        <h3 className="text-2xl font-bold">{currentUserData.agent_name}</h3>
                        <p className="text-blue-200 text-sm capitalize">{currentUserData.role}</p>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
                      <div className="text-center lg:text-left">
                        <p className="text-3xl md:text-4xl font-black text-white">{currentUserData.current_month}</p>
                        <p className="text-xs text-blue-200 uppercase tracking-wider">Sales This Month</p>
                      </div>
                      <div className="text-center lg:text-left">
                        <p className={`text-3xl md:text-4xl font-black ${currentUserData.growth >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                          {currentUserData.growth > 0 ? '+' : ''}{currentUserData.growth}%
                        </p>
                        <p className="text-xs text-blue-200 uppercase tracking-wider">Growth</p>
                      </div>
                      <div className="text-center lg:text-left">
                        <p className="text-3xl md:text-4xl font-black text-amber-300">{currentUserData.daily_average}</p>
                        <p className="text-xs text-blue-200 uppercase tracking-wider">Daily Average</p>
                      </div>
                      {gapToNextRank > 0 && (
                        <div className="text-center lg:text-left">
                          <p className="text-3xl md:text-4xl font-black text-white">{gapToNextRank}</p>
                          <p className="text-xs text-blue-200 uppercase tracking-wider">To Rank #{currentUserRank - 1}</p>
                        </div>
                      )}
                    </div>

                    {/* Progress to Next Rank */}
                    {gapToNextRank > 0 && userAheadOfCurrentUser && (
                      <div className="w-full lg:w-64 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-amber-300" />
                            <span className="text-sm font-medium text-white">Chase {userAheadOfCurrentUser.agent_name}</span>
                          </div>
                          <span className="text-xs text-blue-200">{getProgressToNextRank()}%</span>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-2.5">
                          <div 
                            className="bg-gradient-to-r from-amber-400 to-yellow-300 h-2.5 rounded-full transition-all"
                            style={{ width: `${getProgressToNextRank()}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-blue-200 mt-2 flex items-center gap-1">
                          <Rocket className="w-3 h-3" />
                          {gapToNextRank} more sales to level up!
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-50 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{leaderboard.length}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Team Members</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-50 rounded-lg">
                  <BarChart2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{totalSales.toLocaleString()}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Total Sales</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-purple-50 rounded-lg">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{avgSales}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Avg per Agent</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-50 rounded-lg">
                  <Award className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {leaderboard.reduce((sum, a) => sum + (a.badges?.length || 0), 0)}
                  </p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Badges Earned</p>
                </div>
              </div>
            </div>
          </div>

          {/* Complete Rankings Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-blue-600" />
                Complete Rankings
              </h2>
              <span className="text-sm text-slate-500 px-3 py-1 bg-white rounded-lg border border-slate-200">
                {viewMode === 'current' ? currentMonth.name : lastMonth.name}
              </span>
            </div>
            
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Rank</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Agent</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sales</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Growth</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Daily Avg</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Badges</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLeaderboard.map((agent, index) => {
                    const rank = index + 1;
                    const sales = viewMode === 'current' ? agent.current_month : agent.last_month;
                    const isCurrentUser = agent.email === user?.email;
                    
                    return (
                      <tr
                        key={agent.email}
                        className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer ${
                          isCurrentUser ? 'bg-blue-50/50' : ''
                        }`}
                        onClick={() => setSelectedAgent(selectedAgent?.email === agent.email ? null : agent)}
                      >
                        {/* Rank */}
                        <td className="py-4 px-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${
                            rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white shadow-sm' :
                            rank === 2 ? 'bg-gradient-to-br from-slate-200 to-slate-400 text-white' :
                            rank === 3 ? 'bg-gradient-to-br from-orange-300 to-amber-500 text-white' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {rank <= 3 ? (
                              rank === 1 ? <Crown className="w-5 h-5" /> : <Medal className="w-4 h-4" />
                            ) : rank}
                          </div>
                        </td>

                        {/* Agent Info */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white text-sm ${
                              rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-500' :
                              rank === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-500' :
                              rank === 3 ? 'bg-gradient-to-br from-orange-300 to-amber-500' :
                              'bg-gradient-to-br from-blue-500 to-blue-600'
                            }`}>
                              {getInitials(agent.agent_name)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-800">{agent.agent_name}</span>
                                {isCurrentUser && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">You</span>
                                )}
                              </div>
                              <span className="text-sm text-slate-500 capitalize">{agent.role}</span>
                            </div>
                          </div>
                        </td>

                        {/* Sales */}
                        <td className="py-4 px-4 text-right">
                          <span className="text-xl font-bold text-slate-800">{sales}</span>
                        </td>

                        {/* Growth */}
                        <td className="py-4 px-4 text-right hidden sm:table-cell">
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-semibold ${
                            agent.growth > 0 ? 'bg-emerald-50 text-emerald-600' : 
                            agent.growth < 0 ? 'bg-red-50 text-red-600' : 
                            'bg-slate-50 text-slate-500'
                          }`}>
                            {agent.growth > 0 ? <ChevronUp className="w-4 h-4" /> : agent.growth < 0 ? <ChevronDown className="w-4 h-4" /> : null}
                            {agent.growth > 0 ? '+' : ''}{agent.growth}%
                          </div>
                        </td>

                        {/* Daily Average */}
                        <td className="py-4 px-4 text-right hidden md:table-cell">
                          <span className="text-sm font-medium text-slate-600">{agent.daily_average}</span>
                        </td>

                        {/* Badges */}
                        <td className="py-4 px-4 hidden lg:table-cell">
                          <div className="flex items-center justify-center gap-1">
                            {(agent.badges || []).slice(0, 3).map((badge, i) => (
                              <span key={i} className="w-7 h-7 bg-slate-100 rounded-lg flex items-center justify-center text-sm" title={badge.name}>
                                {badge.icon}
                              </span>
                            ))}
                            {(agent.badges?.length || 0) > 3 && (
                              <span className="text-xs text-slate-400 ml-1">+{agent.badges.length - 3}</span>
                            )}
                          </div>
                        </td>

                        {/* Arrow */}
                        <td className="py-4 px-4">
                          <ChevronRight className="w-5 h-5 text-slate-300" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Agent Detail Modal */}
        {selectedAgent && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedAgent(null)}
          >
            <div 
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`p-6 ${
                selectedAgent.current_rank === 1 ? 'bg-gradient-to-r from-amber-100 to-yellow-100' :
                selectedAgent.current_rank === 2 ? 'bg-gradient-to-r from-slate-100 to-slate-200' :
                selectedAgent.current_rank === 3 ? 'bg-gradient-to-r from-orange-100 to-amber-100' :
                'bg-gradient-to-r from-blue-50 to-indigo-50'
              }`}>
                <button 
                  onClick={() => setSelectedAgent(null)}
                  className="absolute top-4 right-4 p-2 hover:bg-black/10 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-xl text-white ${
                    selectedAgent.current_rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-500' :
                    selectedAgent.current_rank === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-500' :
                    selectedAgent.current_rank === 3 ? 'bg-gradient-to-br from-orange-300 to-amber-500' :
                    'bg-gradient-to-br from-blue-500 to-indigo-600'
                  }`}>
                    {getInitials(selectedAgent.agent_name)}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">{selectedAgent.agent_name}</h3>
                    <p className="text-slate-600">Rank #{selectedAgent.current_rank} • {selectedAgent.role}</p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="p-6 border-b border-slate-100">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-800">{selectedAgent.current_month}</p>
                    <p className="text-xs text-slate-500">This Month</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-500">{selectedAgent.last_month}</p>
                    <p className="text-xs text-slate-500">Last Month</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <p className={`text-2xl font-bold ${selectedAgent.growth >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {selectedAgent.growth > 0 ? '+' : ''}{selectedAgent.growth}%
                    </p>
                    <p className="text-xs text-slate-500">Growth</p>
                  </div>
                </div>
              </div>

              {/* Badges */}
              <div className="p-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-500" />
                  Achievements ({selectedAgent.badges?.length || 0})
                </h4>
                {selectedAgent.badges?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedAgent.badges.map((badge, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                        <span className="text-2xl">{badge.icon}</span>
                        <div>
                          <p className="font-medium text-slate-800">{badge.name}</p>
                          <p className="text-xs text-slate-500">{badge.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400">
                    <Target className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No badges yet</p>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-slate-100 bg-slate-50">
                <Button 
                  className="w-full bg-slate-800 hover:bg-slate-700 text-white"
                  onClick={() => setSelectedAgent(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
        <Footer />
      </main>
    </div>
  );
};

// ChevronDown component for negative growth
const ChevronDown = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m6 9 6 6 6-6"/>
  </svg>
);

export default Leaderboard;
